import { useEffect, useMemo, useState } from 'react';

import { ClassicBlockFacade } from 'modules/sdk/lib/facade/classicReader/blocks/ClassicBlockFacade';
import { getRelativeRect } from 'modules/sdk/lib/facade/overlay/utils';
import { addPaddingToRect } from 'utils/addPaddingToRect';
import { useUpdatedState } from 'utils/updatedState/useHelperState';

import { HIGHLIGHT_PADDING_FRACTION, SELECTION_PADDING_FRACTION } from '../../constants';
import { useClassicReaderZoomFraction } from './classicReaderZoomFractionContext';

export enum BlockOverlayType {
  Sentence = 'SENTENCE',
  Word = 'WORD',
  HoveredSentence = 'HOVERED_SENTENCE',
  Search = 'SEARCH',
  SearchFocused = 'SEARCH_FOCUSED',
  SelectedSentence = 'SELECTED_SENTENCE'
}

type BlockOverlayProps = {
  node: HTMLSpanElement;
  variant: 'selection' | 'highlight';
  overlayType: BlockOverlayType;
  startIndex: number;
  endIndexExclusive: number;
  blockFacade: ClassicBlockFacade;
};

export const HIGHLIGHT_BORDER_RADIUS = 4;

export const BlockOverlays = ({ blockFacade, overlayType, variant, node, startIndex, endIndexExclusive }: BlockOverlayProps) => {
  const zoomFraction = useClassicReaderZoomFraction();

  const blockElement = useUpdatedState(blockFacade.htmlElementState);

  const [refresh, setRefresh] = useState<unknown>({});

  // recompute on window resize
  useEffect(() => {
    const listener = () => setRefresh({});
    window.addEventListener('resize', listener);
    return () => window.removeEventListener('resize', listener);
  }, []);

  // observer changes in node size (this will not work with spans that have inline style)
  useEffect(() => {
    const observer = new ResizeObserver(() => setRefresh({}));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  // Whenever the zoom changes we need to rerender this component. Because measurements happen during render we need to have dom ready for measurement which happens only during second render
  useEffect(() => {
    void zoomFraction;
    setRefresh({});
  }, [zoomFraction]);

  const sortedRects = useMemo(() => {
    if (!node.childNodes.length || blockElement === undefined) return [];
    void refresh;
    const blockElementRect = blockElement.getBoundingClientRect();

    blockFacade.reportNewElementSize(blockElementRect);
    const paddingFraction = variant === 'highlight' ? HIGHLIGHT_PADDING_FRACTION : SELECTION_PADDING_FRACTION;
    const r = new Range();
    const rootTextNode = node.childNodes[0];
    r.setStart(rootTextNode, startIndex);
    r.setEnd(rootTextNode, endIndexExclusive);

    const rects = Array.from(r.getClientRects())
      .map(rect => getRelativeRect(blockElementRect, rect))
      .map(rect => addPaddingToRect(rect, rect.height * paddingFraction));

    rects.sort((a, b) => a.top - b.top);
    return rects;
  }, [refresh, node, startIndex, endIndexExclusive, variant, blockElement, blockFacade]);

  useEffect(() => blockFacade.highlightRects(sortedRects, overlayType), [sortedRects, overlayType, blockFacade]);

  return null;
};
